import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import {
  Bot,
  Sparkles,
  ArrowRight,
  Pill,
  Stethoscope,
  TrendingUp,
  GraduationCap,
  ShoppingBag,
  Hotel,
  Dumbbell,
  Wifi,
  Mic2,
  UserCheck,
  Brain,
  Zap,
  ShieldCheck,
  Database,
  MessageSquare,
  Clock,
} from "lucide-react";
import { CtaBand } from "@/components/sections/CtaBand";
import { ContactBlock } from "@/components/sections/ContactBlock";
import { trackEvent, withUtm } from "@/lib/analytics";
import {
  breadcrumbLd,
  serviceLd,
  faqLd,
  ldScripts,
} from "@/lib/seo-jsonld";

const SITE = "https://kentro.com.br";
const IA_URL = `${SITE}/ia`;
const IA_TITLE = "IAs Kentro — Teste a IA do seu segmento | Kentro";
const IA_DESC =
  "Converse com IAs treinadas por nicho: farmácia, clínica, vendas, faculdade, e-commerce, hotel, academia e provedor. Teste grátis em 30s.";
const IA_FAQ = [
  {
    q: "Como funciona a IA da Kentro por segmento?",
    a: "Cada IA é treinada com FAQ, catálogo, tom de voz e fluxos do segmento (farmácia, clínica, vendas, hotel, etc.) e roda em cima da plataforma omnichannel da Kentro — WhatsApp, Instagram, webchat, e-mail e voz.",
  },
  {
    q: "Preciso integrar a IA com meu sistema atual?",
    a: "Não é obrigatório. A IA já funciona standalone via WhatsApp/webchat. Para casos com CRM, ERP ou agenda, a Kentro integra via API e nativos prontos.",
  },
  {
    q: "Posso testar antes de contratar?",
    a: "Sim. As 10 IAs por segmento estão abertas para teste em 30 segundos, sem cadastro. Depois, um especialista mostra como customizar para o seu negócio.",
  },
];

export const Route = createFileRoute("/ia")({
  head: () => ({
    meta: [
      { title: IA_TITLE },
      { name: "description", content: IA_DESC },
      { property: "og:title", content: "IAs Kentro — Teste agora a IA do seu segmento" },
      {
        property: "og:description",
        content:
          "10 IAs treinadas por nicho, prontas para você conversar. Veja na prática como a Kentro atende, qualifica e vende 24/7.",
      },
      { property: "og:url", content: IA_URL },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: IA_URL }],
    scripts: ldScripts(
      breadcrumbLd([
        { name: "Início", path: "/" },
        { name: "IAs Kentro", path: "/ia" },
      ]),
      serviceLd({
        url: IA_URL,
        name: "IAs Kentro por segmento",
        description: IA_DESC,
        serviceType: "Atendimento com IA generativa por segmento",
      }),
      faqLd(IA_FAQ),
    ),
  }),
  component: IaPage,
});


type IA = {
  name: string;
  segment: string;
  description: string;
  icon: typeof Bot;
  url: string;
  accent: string;
  highlight?: string;
};

const IAS: IA[] = [
  {
    name: "Farmácia",
    segment: "Farmácias e drogarias",
    description: "Tira dúvidas de medicamentos, confirma estoque e encaminha receitas com privacidade.",
    icon: Pill,
    url: "https://formulario.kentro.com.br/farmacia",
    accent: "from-emerald-500/30 to-teal-500/10",
  },
  {
    name: "Dr.",
    segment: "Clínicas e consultórios",
    description: "Agenda consultas, lembra retornos e qualifica primeiro contato com tom acolhedor.",
    icon: Stethoscope,
    url: "https://formulario.kentro.com.br/medico",
    accent: "from-sky-500/30 to-blue-500/10",
  },
  {
    name: "Vendedor",
    segment: "Vendas e comercial",
    description: "Aborda lead frio, descobre dor, mostra preço e marca reunião com o time.",
    icon: TrendingUp,
    url: "https://formulario.kentro.com.br/vendedor",
    accent: "from-orange-500/30 to-amber-500/10",
    highlight: "Mais usada",
  },
  {
    name: "Faculdade",
    segment: "Ensino superior",
    description: "Responde sobre cursos, vestibular, mensalidade e abre matrícula sem filas.",
    icon: GraduationCap,
    url: "https://formulario.kentro.com.br/faculdade",
    accent: "from-indigo-500/30 to-violet-500/10",
  },
  {
    name: "Loja Online",
    segment: "E-commerce",
    description: "Recomenda produto, rastreia pedido, recupera carrinho e finaliza venda no chat.",
    icon: ShoppingBag,
    url: "https://formulario.kentro.com.br/lojavirtual",
    accent: "from-pink-500/30 to-rose-500/10",
  },
  {
    name: "Hotéis",
    segment: "Hotelaria",
    description: "Cota diárias, mostra disponibilidade e fecha reserva direto, sem OTA.",
    icon: Hotel,
    url: "https://formulario.kentro.com.br/hotel",
    accent: "from-cyan-500/30 to-sky-500/10",
  },
  {
    name: "AcademIA",
    segment: "Academias e fitness",
    description: "Apresenta planos, agenda aula experimental e reativa aluno inativo.",
    icon: Dumbbell,
    url: "https://formulario.kentro.com.br/academia",
    accent: "from-lime-500/30 to-green-500/10",
  },
  {
    name: "Provedor",
    segment: "Provedores de internet",
    description: "Vende plano por CEP, abre chamado e faz suporte de 1º nível 24/7.",
    icon: Wifi,
    url: "https://formulario.kentro.com.br/provedor",
    accent: "from-fuchsia-500/30 to-purple-500/10",
  },
  {
    name: "Gusttavo Lima",
    segment: "Persona / celebridade",
    description: "Demonstração de IA com persona personalizada, tom de voz e bordões próprios.",
    icon: Mic2,
    url: "https://formulario.kentro.com.br/gustavolima",
    accent: "from-yellow-500/30 to-orange-500/10",
    highlight: "Demo divertida",
  },
  {
    name: "Eva — SDR",
    segment: "Qualificação de leads",
    description: "Recebe lead novo, faz discovery, pontua interesse e entrega quente pro vendedor.",
    icon: UserCheck,
    url: "https://formulario.kentro.com.br/sdr",
    accent: "from-violet-500/30 to-indigo-500/10",
  },
];

const PILLARS = [
  {
    icon: Brain,
    title: "Treinada no SEU negócio",
    text: "Suba PDFs, planilhas, sites e FAQs. Em horas a IA fala como sua marca, com seus preços e regras.",
  },
  {
    icon: Database,
    title: "Conectada aos seus sistemas",
    text: "ERP, CRM, agenda, estoque, pagamento. A IA consulta e age, não só responde.",
  },
  {
    icon: ShieldCheck,
    title: "Transbordo humano inteligente",
    text: "Quando detecta intenção complexa ou risco, passa pro atendente certo com todo o contexto.",
  },
  {
    icon: Zap,
    title: "Responde em menos de 5 segundos",
    text: "Cache semântico e modelos especializados por nicho — sem aquela espera que mata venda.",
  },
];

const FAQ = [
  {
    q: "Preciso instalar alguma coisa pra testar?",
    a: "Não. Clica em qualquer botão acima, abre o WhatsApp e conversa. Leva menos de 30 segundos.",
  },
  {
    q: "Essas IAs são genéricas?",
    a: "Não. Cada uma foi treinada para o vocabulário, objeções e fluxo de vendas do segmento. A sua será treinada no seu negócio específico.",
  },
  {
    q: "Posso ter uma IA com a minha marca e tom de voz?",
    a: "Sim. A IA Gusttavo Lima é exatamente isso: uma persona customizada. Fazemos o mesmo para a sua empresa.",
  },
  {
    q: "Quanto tempo leva para colocar no ar?",
    a: "Entre 5 e 14 dias, dependendo do volume de conteúdo e integrações. Ativação assistida pelo nosso time.",
  },
];

function IaPage() {
  return (
    <>
      {/* HERO */}
      <section className="gradient-hero relative overflow-hidden pt-28 pb-24">
        <div className="circuit-pattern absolute inset-0 opacity-40" />
        <div className="absolute -left-32 top-20 size-96 rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute -right-32 bottom-0 size-96 rounded-full bg-primary-glow/20 blur-3xl" />

        <div className="relative mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="glass-card inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-medium text-primary-glow">
              <Sparkles className="size-3.5" /> 10 IAs prontas para conversar
            </span>
            <h1 className="mt-6 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
              Pare de imaginar.{" "}
              <span className="gradient-text">Converse com a IA do seu segmento</span> agora.
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
              Escolha o nicho mais parecido com o seu negócio e teste no WhatsApp em menos de
              30 segundos. Sem cadastro, sem cartão, sem instalar nada.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-2">
                <Clock className="size-4 text-primary-glow" /> Resposta em &lt; 5s
              </span>
              <span className="inline-flex items-center gap-2">
                <MessageSquare className="size-4 text-primary-glow" /> WhatsApp real
              </span>
              <span className="inline-flex items-center gap-2">
                <ShieldCheck className="size-4 text-primary-glow" /> Sem compromisso
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* GRID DE IAs */}
      <section id="testar" className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold md:text-4xl">
              Escolha sua IA e <span className="gradient-text">teste agora</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
              Cada IA foi treinada com o vocabulário, objeções e fluxo de vendas do seu segmento.
              O que você vê é exatamente o que seu cliente vai conversar.
            </p>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {IAS.map((ia, i) => {
              const slug = ia.url.split("/").pop() || ia.name.toLowerCase();
              const href = withUtm(ia.url, {
                utm_source: "site_kentro",
                utm_medium: "ia_card",
                utm_campaign: "experimente_ia",
                utm_content: slug,
              });
              const handleClick = () => {
                trackEvent("ia_try_click", {
                  ia_name: ia.name,
                  ia_slug: slug,
                  ia_segment: ia.segment,
                  destination_url: href,
                  location: "ia_page_grid",
                });
              };
              return (
              <motion.a
                key={ia.name}
                href={href}
                onClick={handleClick}
                data-ia-slug={slug}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
                className="glass-card group relative flex flex-col overflow-hidden rounded-2xl p-6 transition hover:-translate-y-1 hover:shadow-glow"
              >
                <div
                  className={`pointer-events-none absolute -right-10 -top-10 size-32 rounded-full bg-gradient-to-br ${ia.accent} blur-2xl transition group-hover:opacity-100`}
                />

                {ia.highlight && (
                  <span className="absolute right-3 top-3 rounded-full bg-primary/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary-glow">
                    {ia.highlight}
                  </span>
                )}

                <div className="relative">
                  <div className="gradient-primary inline-flex size-12 items-center justify-center rounded-xl text-white shadow-glow">
                    <ia.icon className="size-6" />
                  </div>
                  <div className="mt-5 text-xs font-medium uppercase tracking-wider text-primary-glow">
                    {ia.segment}
                  </div>
                  <h3 className="mt-1 text-xl font-semibold">IA {ia.name}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{ia.description}</p>
                </div>

                <div className="relative mt-6 flex items-center justify-between border-t border-white/5 pt-4 text-sm font-medium text-primary-glow">
                  Experimente agora
                  <ArrowRight className="size-4 transition group-hover:translate-x-1" />
                </div>
              </motion.a>
              );
            })}
          </div>

          <p className="mx-auto mt-10 max-w-2xl text-center text-sm text-muted-foreground">
            Não viu seu segmento? <Link to="/contato" className="text-primary-glow underline-offset-4 hover:underline">Fale com a gente</Link> — criamos uma IA sob medida pro seu negócio.
          </p>
        </div>
      </section>

      {/* PILARES */}
      <section className="bg-secondary/30 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold md:text-4xl">
              Por que a IA da Kentro <span className="gradient-text">vende de verdade</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
              IA genérica responde. A nossa entende seu negócio, age nos seus sistemas e passa pro humano na hora certa.
            </p>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {PILLARS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="glass-card rounded-2xl p-6"
              >
                <div className="gradient-primary inline-flex size-11 items-center justify-center rounded-xl text-white shadow-glow">
                  <p.icon className="size-5" />
                </div>
                <h3 className="mt-5 font-semibold">{p.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{p.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* COMO FUNCIONA O TESTE */}
      <section className="py-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold md:text-4xl">Como funciona o teste</h2>
            <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
              3 passos. Nenhum cadastro. Resultado em minutos.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {[
              { n: "01", t: "Escolha o nicho", d: "Clica na IA mais próxima do seu negócio na seção acima." },
              { n: "02", t: "Abre no WhatsApp", d: "O link te leva direto pra uma conversa real, sem formulário." },
              { n: "03", t: "Pergunta o que quiser", d: "Testa objeções, dúvidas e fluxos. Veja como a IA reage de verdade." },
            ].map((s) => (
              <div key={s.n} className="glass-card rounded-2xl p-6">
                <div className="gradient-text text-5xl font-bold">{s.n}</div>
                <h3 className="mt-3 font-semibold">{s.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-secondary/30 py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-center text-3xl font-bold md:text-4xl">Perguntas rápidas</h2>
          <div className="mt-10 space-y-4">
            {FAQ.map((f) => (
              <details key={f.q} className="glass-card group rounded-2xl p-5 [&_summary::-webkit-details-marker]:hidden">
                <summary className="flex cursor-pointer items-center justify-between font-medium">
                  {f.q}
                  <ArrowRight className="size-4 text-primary-glow transition group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
          <p className="mt-6 text-center text-sm text-muted-foreground">
            Outras dúvidas (planos, LGPD, integrações, segmentos):{" "}
            <Link to="/perguntas-frequentes" className="text-primary-glow hover:underline">
              ver todas as perguntas frequentes
            </Link>
            .
          </p>
        </div>
      </section>

      <CtaBand />
      <ContactBlock source="ia" eyebrow="Quer uma IA treinada no seu negócio?" />
    </>
  );
}
